import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthApi } from "../../api/common/apiIndex";
import { IoMailOpenOutline } from "react-icons/io5";
import "./LoginForm.css";
import Loader from "../common/commonUtils/Loader";
import PublicShell from "../common/shell/PublicShell";

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [checking, setChecking] = useState(true);
  const [tokenStatus, setTokenStatus] = useState("CHECKING"); 

  useEffect(() => {
    if (!token) {
      setTokenStatus("INVALID");
      setChecking(false);
      return;
    }

    const validate = async () => {
      try {
        const res = await AuthApi.validateEmailToken({ token }); 
        if (res?.code === "TOKEN_VALID" || res?.status === "VALID") {
          setTokenStatus("VALID");
        } else {
          setTokenStatus(res?.status || "INVALID");
        }
      } catch (err) {
        const code = err?.response?.data?.code;
        if (code === "TOKEN_EXPIRED") setTokenStatus("EXPIRED");
        else if (code === "TOKEN_USED") setTokenStatus("USED");
        else setTokenStatus("INVALID");
      } finally {
        setChecking(false);
      }
    };

    validate();
  }, [token]);

  const verifyMutation = useMutation({
    mutationFn: ({ token }) => AuthApi.verifyEmail({ token }), 
    onSuccess: (res) => {
      const msg = res?.message || "Your email has been verified successfully.";
      toast.success(msg);
      navigate("/login");
    },
    onError: (err) => {
      const code = err?.response?.data?.code;
      if (code === "TOKEN_EXPIRED") {
        toast.error("This verification link has expired.");
        setTokenStatus("EXPIRED");
      } else if (code === "TOKEN_USED") {
        toast.warn("This verification link has already been used.");
        setTokenStatus("USED");
      } else {
        toast.error(err?.response?.data?.message || "Invalid verification link.");
        setTokenStatus("INVALID");
      }
    },
  });

  const handleVerify = (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing verification token.");
      return;
    }
    verifyMutation.mutate({ token });
  };

  if (checking) {
    return (
      <PublicShell className="login-light">
        <Loader text="Validating link..." className="py-5" />
      </PublicShell>
    );
  }

  if (tokenStatus !== "VALID") {
    const isExpired = tokenStatus === "EXPIRED";
    const isUsed = tokenStatus === "USED";
    const title = isExpired
      ? "Link Expired"
      : isUsed
      ? "Link Already Used"
      : "Invalid Link";

    const message = isExpired
      ? "This email verification link has expired. Please request a new one from the login page."
      : isUsed
      ? "This link has already been used. You can log in if your email is already verified."
      : "The verification link is missing or invalid.";

    return (
      <PublicShell className="login-light">
        <div className="card shadow-sm border-0 rounded-4 w-100" style={{ maxWidth: 360 }}>
          <div className="card-body p-3 p-sm-4 text-center">
            <h1 className="h5 fw-semibold mb-3 text-danger">{title}</h1>
            <p className="text-muted small mb-3">{message}</p>
            <button
              onClick={() => navigate("/login")}
              className="btn btn-primary rounded-pill"
            >
              Go to login
            </button>
          </div>
        </div>
      </PublicShell>
    );
  }

  // VALID
  return (
    <PublicShell className="login-light">
      <form
        onSubmit={handleVerify}
        className="card shadow-sm border-0 rounded-4 w-100"
        style={{ maxWidth: 360 }}
      >
        <div className="card-body p-3 p-sm-4">
          <h1 className="h4 text-center fw-semibold mb-3">Verify Email</h1>
          <p className="text-muted small text-center mb-4">
            Click the button below to verify your email and continue.
          </p>

          <button
            type="submit"
            className="btn btn-primary w-100 rounded-3 d-flex align-items-center justify-content-center"
            disabled={verifyMutation.isPending}
          >
            {verifyMutation.isPending && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
            )}
            <IoMailOpenOutline className="me-2" />
            Verify Email
          </button>
        </div>
      </form>
    </PublicShell>
  );
}
